import { type ChangeEvent, type KeyboardEvent, memo, useCallback, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '../../atoms'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  // Accessible name; defaults to "Search"
  label?: string
}

// Lucide "search"; static, so built once
const searchIcon = (
  <svg
    className="search-input__icon"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

// Lucide "x"
const clearIcon = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

// A search box for list pages, cleared with the X button or Escape. It holds no state: pages
// keep the term in useDebounceState and query with its debounced value.
export const SearchInput = memo(({ value, onChange, placeholder, label }: SearchInputProps) => {
  const { t } = useTranslation('admin-mobifull')
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  const clear = useCallback(() => {
    onChange('')
    inputRef.current?.focus()
  }, [onChange])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' && e.currentTarget.value) {
        e.preventDefault()
        clear()
      }
    },
    [clear],
  )

  return (
    <div className="search-input">
      {searchIcon}
      <Input
        ref={inputRef}
        id={id}
        type="search"
        className="search-input__field"
        value={value}
        placeholder={placeholder ?? t('common.search.placeholder')}
        aria-label={label ?? t('common.search.label')}
        autoComplete="off"
        spellCheck={false}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {value ? (
        <Button
          variant="ghost"
          className="search-input__clear"
          aria-label={t('common.search.clear')}
          onClick={clear}>
          {clearIcon}
        </Button>
      ) : null}
    </div>
  )
})

SearchInput.displayName = 'SearchInput'
